import { Injectable, inject, computed, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, from, of } from 'rxjs';
import { AuthService } from './auth.service';
import { BranchService } from './branch.service';
import { UserRole, BranchCategory } from '../models/enums';
import { VoucherService } from './voucher.service';
import { CuentaCorrienteService } from './cuenta-corriente.service';
import { configuracionVale, mostrarMenuVales } from '../utils/voucher.util';
import { mostrarSubcategoriasEnMenu } from '../utils/subcategorias.util';
import { mostrarModuloCc, totalCcHabilitada } from '../utils/cc-visibility.util';
import { cambioDeTurnoDeEmpresa } from '../utils/cash-shift.util';

export interface MenuItem {
    label: string;
    icon: string;
    route?: string;
    children?: MenuItem[];
    requiredFeature?: string;
}

export const VERTICAL_CONFIG: Record<BranchCategory, { resourceLabel: string; resourceIcon: string; newItemLabel: string; subtitle: string; namePlaceholder: string }> = {
    [BranchCategory.BEAUTY]: {
        resourceLabel: 'Zonas / Gabinetes',
        resourceIcon: 'face',
        newItemLabel: 'Zona',
        subtitle: 'Gestión de gabinetes y áreas de atención',
        namePlaceholder: 'Ej. Gabinete 1, Zona Zen'
    },
    [BranchCategory.SPORTS]: {
        resourceLabel: 'Canchas / Espacios',
        resourceIcon: 'sports_tennis',
        newItemLabel: 'Cancha',
        subtitle: 'Gestión de canchas, pistas y espacios deportivos',
        namePlaceholder: 'Ej. Cancha de Tenis 1, Pista de Pádel'
    },
    [BranchCategory.HEALTH]: {
        resourceLabel: 'Camas / Consultorios',
        resourceIcon: 'medical_services',
        newItemLabel: 'Consultorio',
        subtitle: 'Gestión de unidades de atención y equipamiento',
        namePlaceholder: 'Ej. Consultorio 102, Camilla de Masajes'
    },
    [BranchCategory.GYM]: {
        resourceLabel: 'Salas / Cupos',
        resourceIcon: 'fitness_center',
        newItemLabel: 'Sala',
        subtitle: 'Gestión de espacios y límites de capacidad',
        namePlaceholder: 'Ej. Sala de Yoga, Área de Funcional'
    },
    [BranchCategory.GENERAL]: {
        resourceLabel: 'Recursos',
        resourceIcon: 'inventory',
        newItemLabel: 'Recurso',
        subtitle: 'Gestión de recursos operativos',
        namePlaceholder: 'Ej. Recurso A, Espacio 1'
    }
};

@Injectable({
    providedIn: 'root'
})
export class NavigationService {
    private auth = inject(AuthService);
    private branchService = inject(BranchService);
    private voucherService = inject(VoucherService);
    private ccService = inject(CuentaCorrienteService);

    /**
     * Vales activos sin usar en la organización (el listado de vales muestra los de todas las sucursales).
     * Solo se consulta con el vale apagado: el menú sigue mostrando "Vales" mientras queden (D20).
     */
    private readonly activeVouchersResource = rxResource({
        params: () => ({
            check: this.auth.hasFeature('sales') && !configuracionVale(this.auth.businessSettings()).enabled
        }),
        stream: ({ params }) => params.check
            // Si la consulta falla, el menú no se rompe: solo no muestra "Vales".
            ? from(this.voucherService.hasActiveVouchers(null)).pipe(catchError(() => of(false)))
            : of(false)
    });
    
    /**
     * ¿Hay cuentas corrientes de clientes con saldo? Solo se consulta con Total C.C. deshabilitada:
     * si queda deuda o saldo a favor el módulo C.C. sigue visible para cobrarlo o consultarlo.
     */
    private readonly cuentasConSaldoResource = rxResource({
        params: () => ({
            businessId: this.auth.userProfile()?.business_id ?? null,
            check: !totalCcHabilitada(this.auth.businessSettings())
        }),
        stream: ({ params }) => params.check && params.businessId
            // Si la consulta falla, el menú no se rompe: solo no muestra "C.C.".
            ? from(this.ccService.hayCuentasConSaldo(params.businessId)).pipe(catchError(() => of(false)))
            : of(false)
    });

    /** El Superadmin ve siempre el módulo C.C.; el resto según la modalidad Total C.C. y los saldos. */
    readonly moduloCcVisible = computed(() =>
        this.auth.userRole() === UserRole.SUPERADMIN
        || mostrarModuloCc(this.auth.businessSettings(), this.cuentasConSaldoResource.value())
    );

    /** Recalcula si quedan cuentas con saldo (llamar después de cobrar una deuda). */
    refrescarCuentasConSaldo(): void {
        this.cuentasConSaldoResource.reload();
    }

    // Sidebar Collapsed State
    readonly isSidebarCollapsed = signal<boolean>(false);

    /**
     * @description Toggle the sidebar collapse state.
     */
    toggleSidebar(): void {
        this.isSidebarCollapsed.update(state => !state);
    }

    readonly activeConfig = computed(() => {
        const category = this.branchService.activeCategory();
        return VERTICAL_CONFIG[category] || VERTICAL_CONFIG[BranchCategory.GENERAL];
    });

    readonly menuItems = computed(() => {
        const role = this.auth.userRole() as UserRole;
        const isEnabled = this.auth.businessEnabled();
        const category = this.branchService.activeCategory();
        const items: MenuItem[] = [];

        if (!role) return items;

        // Si la empresa NO está habilitada y NO es Superadmin, solo mostrar Mi Organización
        if (role !== UserRole.SUPERADMIN && !isEnabled) {
            items.push({ 
                label: 'Mi Organización', 
                icon: 'corporate_fare', 
                route: '/admin/my-organization' 
            });
            return items;
        }

        // 1. Dashboard (Condicional según rol - oculto para rol Operativo/Recepción)
        if (role !== UserRole.USER && role !== UserRole.POS) {
            const dashboardRoute = [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER].includes(role) 
                ? '/admin/dashboard' 
                : '/dashboard';
            items.push({ label: 'Dashboard', icon: 'dashboard', route: dashboardRoute });
        }

        // 1b. Finanzas (Restringido por 'finance')
        if (this.auth.hasFeature('finance')) {
            if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER].includes(role)) {
                items.push({ 
                    label: 'Finanzas', 
                    icon: 'payments', 
                    children: [
                        { label: 'Caja Diaria', icon: 'account_balance_wallet', route: '/finance/cash/daily' },
                        { label: 'Historial de Cajas', icon: 'history', route: '/finance/cash/history' },
                        { label: 'Movimientos', icon: 'swap_vert', route: '/finance/cash/movements' },
                        // La auditoría de turnos aparece solo si la empresa tiene habilitado el cambio de turno
                        ...(cambioDeTurnoDeEmpresa(this.auth.businessSettings())
                            ? [{ label: 'Auditoría de Turnos', icon: 'fact_check', route: '/finance/cash/shift-audit' }]
                            : []),
                        { label: 'Gastos del Mes', icon: 'receipt_long', route: '/finance/expenses/monthly' },
                        { label: 'Plantillas de Costos Fijos', icon: 'event_repeat', route: '/finance/expenses/templates' }
                    ]
                });
            } else if ([UserRole.POS].includes(role)) {
                items.push({ 
                    label: 'Finanzas', 
                    icon: 'payments', 
                    children: [
                        { label: 'Caja Diaria', icon: 'account_balance_wallet', route: '/finance/cash/daily' },
                        { label: 'Historial de Cajas', icon: 'history', route: '/finance/cash/history' },
                        { label: 'Movimientos', icon: 'swap_vert', route: '/finance/cash/movements' }
                    ]
                });
            } else if ([UserRole.RESOURCE, UserRole.USER].includes(role)) {
                items.push({ 
                    label: 'Finanzas', 
                    icon: 'payments', 
                    children: [
                        { label: 'Caja Diaria', icon: 'account_balance_wallet', route: '/finance/cash/daily' },
                        { label: 'Gastos del Mes', icon: 'receipt_long', route: '/finance/expenses/monthly' },
                        { label: 'Plantillas de Costos Fijos', icon: 'event_repeat', route: '/finance/expenses/templates' }
                    ]
                });
            }
        }

        // 1c. Inventario (Restringido por 'products') + Distribución ('distribution'),
        // cada uno independiente entre sí.
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role)
            && (this.auth.hasFeature('products') || this.auth.hasFeature('distribution'))) {
            const inventoryChildren: MenuItem[] = [];

            if (this.auth.hasFeature('products')) {
                inventoryChildren.push(
                    { label: 'Productos', icon: 'inventory', route: '/admin/inventory' },
                    { label: 'Categorías', icon: 'category', route: '/admin/inventory/categories' },
                );
                // Subcategorías: solo si la empresa las usa (settings.subcategories.enabled), entre Categorías y Distribución.
                if (mostrarSubcategoriasEnMenu(this.auth.businessSettings(), true)) {
                    inventoryChildren.push({ label: 'Subcategorías', icon: 'account_tree', route: '/admin/inventory/subcategories' });
                }
            }

            if (this.auth.hasFeature('distribution')) {
                inventoryChildren.push(
                    { label: 'Distribución', icon: 'sync_alt', route: '/admin/inventory/distribution' },
                );
            }

            items.push({
                label: 'Inventario',
                icon: 'inventory_2',
                children: inventoryChildren
            });
        }

        // 1c-bis. Proveedor (Restringido por 'suppliers'), sección propia separada de Inventario.
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role)
            && this.auth.hasFeature('suppliers')) {
            items.push({
                label: 'Proveedor',
                icon: 'store',
                children: [
                    { label: 'Listado', icon: 'list', route: '/admin/suppliers' },
                    { label: 'Nueva Recepción', icon: 'upload_file', route: '/admin/supplier-receipts/new' },
                    { label: 'C.C.', icon: 'account_balance', route: '/admin/suppliers/cc' }
                ]
            });
        }

        // 1d. Ventas (Restringido por 'sales')
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role) && this.auth.hasFeature('sales')) {
            items.push({ 
                label: 'Ventas', 
                icon: 'shopping_cart', 
                children: [
                    { label: 'Nueva Venta', icon: 'add_shopping_cart', route: '/admin/sales/new' },
                    { label: 'Ventas Realizadas', icon: 'history', route: '/admin/sales' },
                    // Vales de saldo a favor: vale activo, o apagado pero con vales sin usar (D20).
                    ...(mostrarMenuVales(this.auth.businessSettings(), this.activeVouchersResource.value())
                        ? [{ label: 'Vales', icon: 'confirmation_number', route: '/admin/sales/vouchers' }]
                        : [])
                ]
            });
        }

        // 1e. Facturación (Restringido por 'billing')
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role) && this.auth.hasFeature('billing')) {
            const billingChildren: MenuItem[] = [
                { label: 'Historial', icon: 'receipt_long', route: '/billing' }
            ];
            if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER].includes(role)) {
                billingChildren.push({ label: 'Programadas', icon: 'update', route: '/billing/recurring' });
            }
            items.push({ 
                label: 'Facturación', 
                icon: 'receipt_long', 
                children: billingChildren
            });
        }

        // 2. Empresas (Superadmin only)
        if (role === UserRole.SUPERADMIN) {
            items.push({ label: 'Empresas', icon: 'business', route: '/business' });
        }



        // 3. Sucursales (Superadmin, Admin, Owner)
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER].includes(role)) {
            items.push({ label: 'Sucursales', icon: 'storefront', route: '/branches' });
        }

        // 4. Staff (Superadmin, Admin, Owner)
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.OWNER].includes(role)) {
            items.push({ label: 'Staff', icon: 'people', route: '/staff' });
        }

        // 4b. Clientes (Superadmin, Admin, Owner, Resource, User)
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role)) {
            const clientChildren: MenuItem[] = [
                { label: 'Listado', icon: 'list', route: '/admin/clients' }
            ];
            if (role !== UserRole.POS && this.moduloCcVisible()) {
                clientChildren.push({ label: 'C.C.', icon: 'account_balance', route: '/clientes/cc' });
            }
            items.push({ 
                label: 'Clientes', 
                icon: 'group', 
                children: clientChildren
            });
        }

        // 4c. Formularios (Superadmin, Admin, Owner, Resource, User) (Restringido por 'forms')
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role) && this.auth.hasFeature('forms')) {
            items.push({ label: 'Formularios', icon: 'assignment', route: '/forms' });
        }

        // 5. RECURSOS (Dinámico) (Restringido por 'resources')
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role) && this.auth.hasFeature('resources')) {
            const config = VERTICAL_CONFIG[category] || VERTICAL_CONFIG[BranchCategory.GENERAL];
            items.push({
                label: config.resourceLabel,
                icon: config.resourceIcon,
                route: '/resources'
            });
        }

        // 6. Servicios (Superadmin, Admin, Owner, Resource, User) (Restringido por 'services')
        if ([UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.POS, UserRole.OWNER, UserRole.RESOURCE, UserRole.USER].includes(role) && this.auth.hasFeature('services')) {
            items.push({ label: 'Servicios', icon: 'spa', route: '/services' });
        }

        // 7. Agenda (Todos) (Restringido por 'booking')
        if (this.auth.hasFeature('booking')) {
            items.push({ label: 'Agenda', icon: 'calendar_today', route: '/admin/my-agenda' });
        }

        // 8. Configuración (Superadmin, Owner, Admin, POS)
        if ([UserRole.SUPERADMIN, UserRole.OWNER, UserRole.ADMIN, UserRole.POS].includes(role)) {
            const configChildren = [];
            if (role === UserRole.OWNER) {
                configChildren.push({ label: 'Organización', icon: 'corporate_fare', route: '/admin/my-organization' });
            }
            if ([UserRole.SUPERADMIN, UserRole.OWNER, UserRole.ADMIN].includes(role)) {
                configChildren.push({ label: 'Formas de Pago', icon: 'payments', route: '/admin/payment-methods' });
            }
            configChildren.push({ label: 'Categorías de Caja', icon: 'label', route: '/admin/config/categories' });
            if (role !== UserRole.POS) {
                configChildren.push({ label: 'Categorías de Gastos Admin.', icon: 'folder_special', route: '/admin/config/administrative-categories' });
            }

            items.push({ 
                label: 'Configuración', 
                icon: 'settings', 
                children: configChildren
            });
        }

        return items;
    });
}
